/**
 * Product Image Zoom
 * Handles zoom modal for individual product images
 */

(function () {
  'use strict';

  const modal = document.getElementById('zoomModal');
  if (!modal) return;

  const closeButton = modal.querySelector('.product__zoom-close');
  const zoomImage = modal.querySelector('.product__zoom-image');
  const zoomMessage = modal.querySelector('.product__zoom-message p');
  let currentScale = 1;
  let startX = 0;
  let startY = 0;
  let translateX = 0;
  let translateY = 0;
  let isDragging = false;
  let isPinching = false;

  // Detect if device supports touch
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Open zoom modal
  function openZoomModal(imageUrl, altText) {
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    zoomImage.src = imageUrl;
    zoomImage.alt = altText;
    
    // Update message text based on device
    if (zoomMessage) {
      zoomMessage.textContent = isTouchDevice ? 'Pinch to Zoom' : 'Scroll to Zoom';
      
      // Add flash animation
      const messageContainer = modal.querySelector('.product__zoom-message');
      if (messageContainer) {
        messageContainer.classList.remove('flash');
        // Force reflow to restart animation
        void messageContainer.offsetWidth;
        messageContainer.classList.add('flash');
      }
    }
    
    resetZoom();
  }

  // Close modal function
  function closeModal() {
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    resetZoom();
  }

  // Reset zoom state
  function resetZoom() {
    currentScale = 1;
    translateX = 0;
    translateY = 0;
    isDragging = false;
    isPinching = false;
    updateTransform();
  }

  // Update transform
  function updateTransform() {
    if (zoomImage) {
      zoomImage.style.transform = `translate(${translateX}px, ${translateY}px) scale(${currentScale})`;
      zoomImage.style.cursor = currentScale > 1 ? 'grab' : 'default';
    }
  }

  // Start drag
  function startDrag(e) {
    if (currentScale <= 1 || isPinching) return;
    
    e.preventDefault();
    isDragging = true;
    
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    
    startX = clientX - translateX;
    startY = clientY - translateY;
    
    if (zoomImage) zoomImage.style.cursor = 'grabbing';
  }

  // Drag
  function drag(e) {
    if (!isDragging || isPinching) return;
    
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    
    translateX = clientX - startX;
    translateY = clientY - startY;
    updateTransform();
  }

  // End drag
  function endDrag() {
    if (!isDragging) return;
    isDragging = false;
    
    if (zoomImage) zoomImage.style.cursor = currentScale > 1 ? 'grab' : 'default';
  }

  // Touch handlers for pinch zoom and double tap
  let initialDistance = 0;
  let initialScale = 1;
  let lastTap = 0;

  function handleTouchStart(e) {
    if (e.touches.length === 2) {
      // Start pinch zoom
      e.preventDefault();
      isPinching = true;
      initialDistance = getDistance(e.touches[0], e.touches[1]);
      initialScale = currentScale;
    } else if (e.touches.length === 1 && currentScale > 1) {
      // Start drag when zoomed
      startDrag(e);
    }
  }

  function handleTouchMove(e) {
    if (e.touches.length === 2 && isPinching) {
      e.preventDefault();
      const currentDistance = getDistance(e.touches[0], e.touches[1]);
      currentScale = Math.min(Math.max(1, initialScale * (currentDistance / initialDistance)), 4);
      updateTransform();
    } else if (e.touches.length === 1 && isDragging) {
      e.preventDefault();
      drag(e);
    }
  }

  function handleTouchEnd(e) {
    if (isPinching) {
      isPinching = false;
      if (currentScale <= 1) {
        resetZoom();
      }
    }

    if (isDragging) {
      endDrag();
    }

    // Double-tap to zoom
    if (e.touches.length === 0 && !isPinching) {
      const currentTime = new Date().getTime();
      const tapLength = currentTime - lastTap;
      
      if (tapLength < 300 && tapLength > 0) {
        e.preventDefault();
        if (currentScale === 1) {
          currentScale = 2;
          translateX = 0;
          translateY = 0;
        } else {
          resetZoom();
        }
        updateTransform();
      }
      
      lastTap = currentTime;
    }
  }

  function getDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Mouse wheel zoom on desktop
  function handleWheel(e) {
    if (modal.getAttribute('aria-hidden') === 'true') return;
    
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    currentScale = Math.min(Math.max(1, currentScale + delta), 4);
    
    if (currentScale === 1) {
      translateX = 0;
      translateY = 0;
    }
    
    updateTransform();
  }

  // Event listeners
  document.addEventListener('click', function (event) {
    const zoomButton = event.target.closest('.product__zoom-icon');
    if (!zoomButton) return;

    event.preventDefault();
    
    // Get image URL and alt text from the zoom button data attribute or sibling image
    const imageUrl = zoomButton.dataset.zoomImage;
    const imgElement = zoomButton.previousElementSibling?.querySelector('img');
    const altText = imgElement?.alt || '';
    
    openZoomModal(imageUrl, altText);
  });

  if (closeButton) {
    closeButton.addEventListener('click', closeModal);
  }

  modal.addEventListener('click', function (event) {
    if (event.target === modal || event.target.classList.contains('product__zoom-content')) {
      closeModal();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
      closeModal();
    }
  });

  // Attach event handlers to zoom image
  if (zoomImage) {
    zoomImage.addEventListener('mousedown', startDrag);
    zoomImage.addEventListener('touchstart', handleTouchStart, { passive: false });
  }

  document.addEventListener('mousemove', drag);
  document.addEventListener('mouseup', endDrag);
  document.addEventListener('touchmove', handleTouchMove, { passive: false });
  document.addEventListener('touchend', handleTouchEnd);
  modal.addEventListener('wheel', handleWheel, { passive: false });
})();
